<template>
  <div class="summary-container d-flex flex-column">

    <div v-if="isLoading" class="text-center">

      <v-skeleton-loader type="sentences, paragraph, sentences, paragraph, paragraph, paragraph"
        class="pb-12"></v-skeleton-loader>


      <!-- <v-progress-circular
              indeterminate
              color="primary"
              :size="70"
              :width="7"
            ></v-progress-circular>
            <p class="mt-4">Cargando resumen...</p> -->
    </div>

    <div v-else-if="error" class="text-center my-12">
      <v-alert type="error" outlined>
        {{ error }}
      </v-alert>
      <v-btn color="primary" class="mt-4" @click="fetchSummary">
        Intentar de nuevo
      </v-btn>
    </div>

    <div v-else-if="!summary" class="text-center my-12">
      <v-alert color="#458553" style="color: white">
        No se encontró ningún resumen para este documento.
      </v-alert>
      <v-btn color="primary" class="mt-4" @click="generateSummary">
        Generar resumen
      </v-btn>
    </div>

    <v-row v-else>

      <v-btn @click="printSummary" color="#E8E8E8" text-color="#6E8071" class="edit-btn rounded-xl" height="36px" small
        depressed>

        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#6E8071"
          stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
          class="lucide lucide-printer-icon lucide-printer">
          <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
          <path d="M6 9V3a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v6" />
          <rect x="6" y="14" width="12" height="8" rx="1" />
        </svg>
      </v-btn>


      <v-col cols="12" class="d-flex flex-column rounded-0">
        <v-card flat class="rounded-0">
          <v-card-text class="pa-0 pb-6 summary-wrapper" style="position: relative;">
            <template v-if="isStructuredSummary">
              <div class="custom-expansion-panels mt-4">
                <div v-for="(block, idx) in summary.blocks" :key="idx" class="custom-expansion-panel">
                  <div class="custom-panel-header" 
                  :style="'background-color: ' + (expandedPanels.includes(idx) ? 'rgb(246 246 246)' : '#fafafa') + '!important'"
                  @click="togglePanel(idx)">
                    <span class="custom-panel-title">{{ block.name }}</span>
                    <v-icon>{{ expandedPanels.includes(idx) ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                  </div>
                  <transition name="slide-fade">
                    <div v-if="expandedPanels.includes(idx)" class="custom-panel-content pt-3">
                      <markdown-it-vue :content="block.content.replace(/\\n/g, '\n')" :options="markdownOptions" class="markdown-summary" />
                    </div>
                  </transition>
                </div>
              </div>
            </template>
            <template v-else>
              <div class="summary-content" :class="{ 'summary-content-limited': !showFullSummary }">
                <markdown-it-vue :content="summary.replace(/\\n/g, '\n')" :options="markdownOptions" class="markdown-summary"
                  @rendered="onMarkdownRendered" />
                <div v-if="!showFullSummary" class="blur-overlay"></div>
              </div>
              
              <div class="summary-buttons-container">
                <div v-if="!showFullSummary" class="text-center mt-6">
                  <v-btn color="#4dd453" class="expand-button white--text font-weight-medium"
                    @click="showFullSummary = !showFullSummary">
                    Ver resumen
                    <v-icon right>
                      mdi-chevron-down
                    </v-icon>
                  </v-btn>
                </div>
                
                <div v-else class="text-center mt-6">
                  <v-btn color="#4dd453" text class="summary-close-button white--text font-weight-medium rounded-lg"
                    @click="showFullSummary = !showFullSummary">
                    Cerrar resumen
                    <v-icon right>
                      mdi-chevron-up
                    </v-icon>
                  </v-btn>
                </div>
              </div>
            </template>
          </v-card-text>

        </v-card>

        <v-expand-transition>
          <div v-if="focusInputVisible" class="mt-4">
            <v-card>
              <v-card-title>Generar resumen enfocado</v-card-title>
              <v-card-text>
                <v-text-field v-model="focusQuery" label="Tema específico a resumir" outlined
                  placeholder="Ej: Capítulo 3, Conclusiones, etc."></v-text-field>
                <v-slider v-model="maxLength" label="Longitud máxima (palabras)" min="100" max="1000" step="50"
                  thumb-label></v-slider>
              </v-card-text>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn text @click="focusInputVisible = false">
                  Cancelar
                </v-btn>
                <v-btn color="primary" :disabled="!focusQuery" @click="generateFocusedSummary">
                  Generar
                </v-btn>
              </v-card-actions>
            </v-card>
          </div>
        </v-expand-transition>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import MarkdownItVue from 'markdown-it-vue';
import 'markdown-it-vue/dist/markdown-it-vue.css';

export default {
  name: 'SummaryView',
  components: {
    MarkdownItVue
  },
  props: {
    documentId: {
      type: [String, Number],
      required: true
    }
  },
  data() {
    return {
      documentTitle: '',
      summary: '',
      isLoading: true,
      error: null,
      focusInputVisible: false,
      focusQuery: '',
      maxLength: 500,
      markdownOptions: {
        markdownIt: {
          linkify: false,
          html: true,
          typographer: true,
          breaks: true,
        },
        plugins: [
          ['markdown-it-katex', {
            throwOnError: false,
            displayMode: true,
            output: 'html',
            macros: {},
            minRuleThickness: 0.05,
            strict: false
          }],
          'markdown-it-attrs',
          'markdown-it-sub',
          'markdown-it-sup'
        ]
      },
      showFullSummary: false,
      isStructuredSummary: true,
      expandedPanels: []
    };
  },
  created() {
    this.fetchSummary();
  },
  methods: {
    async fetchSummary() {
      this.isLoading = true;
      this.error = null;

      try {
        const response = await this.axios.get(`/summary/${this.documentId}`, {
          headers: {
            Authorization: this.$root.token.value
          }
        });

        if (response.data.success) {
          if (response.data.summary && response.data.summary.length > 0)
            this.summary = JSON.parse(response.data.summary);
          else
            this.summary = null;

          this.documentTitle = response.data.documentTitle || 'Resumen';
          // this.isStructuredSummary = response.data.isStructuredSummary;
        } else {
          this.error = response.data.error || 'No se pudo cargar el resumen';
        }
      } catch (err) {
        console.error('Error al cargar el resumen:', err);
        this.error = 'Error al cargar el resumen. Por favor, inténtalo de nuevo.';
      } finally {
        this.isLoading = false;
      }
    },

    async generateSummary() {
      this.isLoading = true;
      this.error = null;

      try {
        const response = await this.axios.post('/generate-summary', {
          documentId: this.documentId,
          user_id: this.$root.currentUser.userId
        }, {
          headers: {
            Authorization: this.$root.token.value
          }
        });

        if (response.data.success) {
          this.summary = response.data.summary;
          this.documentTitle = response.data.fileName || 'Resumen';
        } else {
          this.error = response.data.error || 'No se pudo generar el resumen';
        }
      } catch (err) {
        console.error('Error al generar el resumen:', err);
        this.error = 'Error al generar el resumen. Por favor, inténtalo de nuevo.';
      } finally {
        this.isLoading = false;
      }
    },

    async generateFocusedSummary() {
      if (!this.focusQuery) return;

      this.isLoading = true;
      this.error = null;

      try {
        const response = await this.axios.post('/generate-summary', {
          documentId: this.documentId,
          focus: this.focusQuery,
          maxLength: this.maxLength,
          user_id: this.$root.currentUser.userId
        }, {
          headers: {
            Authorization: this.$root.token.value
          }
        });

        if (response.data.success) {
          this.summary = response.data.summary;
          this.focusInputVisible = false;
          this.focusQuery = '';
        } else {
          this.error = response.data.error || 'No se pudo generar el resumen enfocado';
        }
      } catch (err) {
        console.error('Error al generar el resumen enfocado:', err);
        this.error = 'Error al generar el resumen enfocado. Por favor, inténtalo de nuevo.';
      } finally {
        this.isLoading = false;
      }
    },

    downloadSummary() {
      if (!this.summary) return;

      const filename = `resumen-${this.documentTitle.replace(/\s+/g, '-').toLowerCase()}.md`;
      const blob = new Blob([this.summary], { type: 'text/markdown' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    },

    printSummary() {
      if (!this.summary) return;

      // Obtener el HTML renderizado del componente markdown
      const renderedContent = document.querySelector('.markdown-summary .markdown-body').innerHTML;
      const documentTitle = this.documentTitle || 'Resumen';

      // Crear un iframe oculto para imprimir directamente
      const printFrame = document.createElement('iframe');
      printFrame.style.position = 'fixed';
      printFrame.style.right = '0';
      printFrame.style.bottom = '0';
      printFrame.style.width = '0';
      printFrame.style.height = '0';
      printFrame.style.border = '0';

      document.body.appendChild(printFrame);

      printFrame.contentDocument.write(`
        <html>
          <head>
            <title>Resumen: ${documentTitle}</title>
            <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/katex@0.16.0/dist/katex.min.css">
            <style>
              body {
                font-family: Arial, sans-serif;
                line-height: 1.6;
                color: #333;
                max-width: 21cm;
                margin: 0 auto;
                padding: 2cm;
                box-shadow: 0 0 0.5cm rgba(0, 0, 0, 0.1);
                border-left: 1px solid #ddd;
                border-right: 1px solid #ddd;
                background-color: white;
              }
              h1 {
                text-align: center;
                margin-bottom: 20px;
                color: #2c3e50;
              }
              .content {
                max-width: 100%;
                margin: 0 auto;
              }
              .content img {
                max-width: 100%;
                height: auto;
              }
              .content pre {
                background-color: #f5f5f5;
                padding: 10px;
                border-radius: 5px;
                overflow-x: auto;
              }
              .content blockquote {
                border-left: 4px solid #ddd;
                padding-left: 16px;
                margin-left: 0;
                color: #666;
              }
              .content table {
                border-collapse: collapse;
                width: 100%;
                margin: 16px 0;
              }
              .content th, .content td {
                border: 1px solid #ddd;
                padding: 8px;
              }
              .content th {
                background-color: #f2f2f2;
              }
              @media print {
                body {
                  margin: 0;
                  padding: 1.5cm;
                  box-shadow: none;
                  max-width: none;
                  border-left: none;
                  border-right: none;
                }
                @page {
                  size: A4;
                  margin: 0;
                  padding: 0;
                  border-left: 1px solid #ddd;
                  border-right: 1px solid #ddd;
                }
                a {
                  text-decoration: none;
                  color: #000;
                }
              }
            </style>
          </head>
          <body>
            <h1>Resumen: ${documentTitle}</h1>
            <div class="content">${renderedContent}</div>
          </body>
        </html>
      `);

      printFrame.contentDocument.close();

      // Esperar a que los estilos y recursos se carguen
      setTimeout(() => {
        printFrame.contentWindow.print();

        // Eliminar el iframe después de imprimir
        setTimeout(() => {
          document.body.removeChild(printFrame);
        }, 500);
      }, 1000);
    },

    onMarkdownRendered(html) {
      // Esta función se usa para manejar eventos después de renderizar el markdown
    },

    togglePanel(index) {
      const position = this.expandedPanels.indexOf(index);
      if (position !== -1) {
        this.expandedPanels.splice(position, 1);
      } else {
        this.expandedPanels.push(index);
      }
    }
  }
};
</script>

<style scoped>
.summary-container {}

.summary-content {
  line-height: 1.6;
  font-size: 16px;
}

.summary-content-limited {
  max-height: 300px;
  overflow: hidden;
  position: relative;
}

.summary-wrapper {
  position: relative;
  isolation: isolate;
}

.summary-buttons-container {
  position: relative;
  isolation: isolate;
  z-index: 10;
}

.markdown-summary>>>.anchor {
  display: none !important;
}

.markdown-summary>>>.katex,
.markdown-summary>>>.katex-display {
  background-color: transparent !important;
  box-shadow: none !important;
}

.markdown-summary>>>.katex-html,
.markdown-summary>>>.katex-mathml {
  background-color: transparent !important;
}

.markdown-summary>>>.accent {
  background-color: transparent !important;
}

.blur-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 120px;
  background: linear-gradient(to bottom, rgba(255, 255, 255, 0), rgba(255, 255, 255, 0.9) 80%, rgba(255, 255, 255, 1));
  z-index: 5;
}

.expand-button {
  border-radius: 10px !important;
  text-transform: none !important;
  letter-spacing: 0 !important;
  font-weight: 600 !important;
  font-size: 0.9rem !important;
  transition: filter 0.2s ease, transform 0.1s ease !important;
  padding: 0 24px !important;
  height: 36px !important;
  background-color: #4DD453 !important;
  box-shadow: 0 4px 0 #59A700 !important;
  margin-top: -10px;
  position: relative;
  color: #fff !important;
}

.summary-close-button {
  max-width: 180px;
  z-index: 100;
}

.print-button {
  border-radius: 10px !important;
  text-transform: none !important;
  letter-spacing: 0 !important;
  font-weight: 600 !important;
  font-size: 0.9rem !important;
  height: 36px !important;
  position: relative;
}

.edit-btn {
  text-transform: none !important;
  letter-spacing: 0 !important;
  font-weight: 500 !important;
  position: absolute;
  top: 13px;
  right: 60px;
  z-index: 10;
}

/* Estilos para paneles personalizados */
.custom-expansion-panels {
  border-radius: 8px;
  overflow: hidden;
  background-color: #fcfcfc;
  border-radius: 10px;
}

.custom-expansion-panel {
  border-bottom: 1px solid #eaeaea;
  background-color: #fcfcfc;
  user-select: none;
}

.custom-expansion-panel:last-child {
  border-bottom: none;
}

.custom-panel-header {
  padding: 16px 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: background-color 0.2s ease;
}

.custom-panel-header:hover {
  background-color: #f9f9f9;
}

.custom-panel-title {
  font-weight: 600;
  color: #333;
  font-size: 1rem;
}

.custom-panel-content {
  padding: 0 20px 20px 20px;
  background-color: #fcfcfc;
  border-top: 1px solid #f0f0f0;
}

/* Transiciones */
.slide-fade-enter-active {
  transition: all 0.3s ease-out;
}

.slide-fade-leave-active {
  transition: all 0.2s cubic-bezier(1, 0.5, 0.8, 1);
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  transform: translateY(-10px);
  opacity: 0;
}
</style>